import React, { useState, useEffect, useRef } from 'react';
import { Person } from '../graph/types';
import { graphService } from '../graph/graphService';
import { AttendeeChip } from './AttendeeChip';

interface PeoplePickerProps {
  selectedPeople: Person[];
  onPeopleChange: (people: Person[]) => void;
}

export const PeoplePicker: React.FC<PeoplePickerProps> = ({
  selectedPeople,
  onPeopleChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<Person[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search for users when search term changes
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await graphService.searchUsers(searchTerm);
        // Filter out already selected people
        const filtered = results.filter(
          (person) => !selectedPeople.some((p) => p.emailAddress === person.emailAddress)
        );

        if (filtered.length === 0) {
          setSuggestions([]);
          setShowSuggestions(false);
          setIsLoading(false);
          return;
        }

        // Check calendar access for all filtered results
        const emailsToCheck = filtered.map((p) => p.emailAddress);
        const accessibleEmails = await graphService.checkCalendarAccess(emailsToCheck);

        // Only show users whose calendars are accessible
        const accessiblePeople = filtered.filter((person) =>
          accessibleEmails.includes(person.emailAddress)
        );

        setSuggestions(accessiblePeople);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Error searching users:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, selectedPeople]);

  const handleSelectPerson = (person: Person) => {
    onPeopleChange([...selectedPeople, person]);
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleRemovePerson = (person: Person) => {
    onPeopleChange(selectedPeople.filter((p) => p.emailAddress !== person.emailAddress));
  };

  return (
    <div className="people-picker-container">
      <label className="section-label">Attendees</label>

      <div className="people-picker" ref={wrapperRef}>
        <input
          type="text"
          className="search-input"
          placeholder="Type a name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        />

        {showSuggestions && suggestions.length > 0 && (
          <div className="suggestions-dropdown">
            {suggestions.map((person) => (
              <div
                key={person.emailAddress}
                className="suggestion-item"
                onClick={() => handleSelectPerson(person)}
              >
                <div className="suggestion-name">{person.displayName}</div>
                <div className="suggestion-email">{person.emailAddress}</div>
              </div>
            ))}
          </div>
        )}

        {isLoading && <div className="loading-indicator">Searching...</div>}
      </div>

      {selectedPeople.length > 0 && (
        <div className="selected-people">
          {selectedPeople.map((person) => (
            <AttendeeChip
              key={person.emailAddress}
              person={person}
              onRemove={handleRemovePerson}
            />
          ))}
        </div>
      )}

      {selectedPeople.length === 0 && (
        <div className="empty-state">No attendees selected</div>
      )}
    </div>
  );
};
